import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, X, Search, Send, Upload, Mail, Eye, RotateCcw, Image, Trash2, Copy, Check } from 'lucide-react';
import { API_URL } from './shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';

const CommunicationsTab = ({ token }) => {
  const [userCounts, setUserCounts] = useState({ all: 0, members_only: 0, pro: 0, club: 0, non_members: 0 });
  const [emailForm, setEmailForm] = useState({ subject: '', message: '', recipient_type: 'all', images: [], individual_emails: [] });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailImageUrl, setEmailImageUrl] = useState('');
  const [uploadingEmailImage, setUploadingEmailImage] = useState(false);
  const emailImageInputRef = useRef(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({ sent: 0, failed: 0, bounced: 0, pending: 0, total: 0 });
  const [emailLogFilter, setEmailLogFilter] = useState('all');
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [communicationUsers, setCommunicationUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [bulkJob, setBulkJob] = useState(null);
  const [allJobs, setAllJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [cancellingJob, setCancellingJob] = useState(false);
  const [viewEmail, setViewEmail] = useState(null);
  const [loadingViewEmail, setLoadingViewEmail] = useState(false);
  // Media Library state
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaDeleting, setMediaDeleting] = useState(null);

  const fetchMediaLibrary = async () => {
    setMediaLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/media`, { headers: { Authorization: `Bearer ${token}` } });
      setMediaItems(res.data?.items || []);
    } catch (error) {
      toast.error('Failed to load media library');
    } finally {
      setMediaLoading(false);
    }
  };

  const openMediaLibrary = () => {
    setMediaOpen(true);
    fetchMediaLibrary();
  };

  const addMediaToEmail = (url) => {
    if (emailForm.images.includes(url)) {
      toast.info('Already added to this email');
      return;
    }
    setEmailForm(prev => ({ ...prev, images: [...prev.images, url] }));
    toast.success('Added to email');
  };

  const copyMediaUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch (_) {
      toast.error('Could not copy — clipboard blocked');
    }
  };

  const deleteMediaItem = async (filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone. Past emails that used it will show a broken image.`)) return;
    setMediaDeleting(filename);
    try {
      await axios.delete(`${API_URL}/admin/media/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMediaItems(prev => prev.filter(m => m.filename !== filename));
      toast.success('Deleted');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
    } finally {
      setMediaDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openEmailView = async (logId) => {
    if (!logId) return;
    setLoadingViewEmail(true);
    setViewEmail({ loading: true });
    try {
      const res = await axios.get(`${API_URL}/admin/email-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewEmail(res.data);
    } catch (error) {
      toast.error('Failed to load email content');
      setViewEmail(null);
    } finally {
      setLoadingViewEmail(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!bulkJob?.id) return;
    const failedCount = bulkJob.unretried_failed ?? bulkJob.failed ?? 0;
    if (!window.confirm(`Re-send to the ${failedCount} un-retried failed recipient(s) only? Successfully delivered recipients will NOT receive a duplicate. The original failed rows will stay visible in the log.`)) return;
    setRetryingFailed(true);
    try {
      const res = await axios.post(`${API_URL}/admin/bulk-email/${bulkJob.id}/retry-failed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || 'Retry queued');
      // Refresh jobs + select the newly-created retry job
      await fetchAllJobs();
      if (res.data?.job_id) setSelectedJobId(res.data.job_id);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to queue retry');
    } finally {
      setRetryingFailed(false);
    }
  };

  const handleCancelJob = async () => {
    if (!bulkJob?.id) return;
    if (!window.confirm('Cancel this running bulk email job? Emails already sent will not be undone. The loop will stop before the next recipient.')) return;
    setCancellingJob(true);
    try {
      await axios.post(`${API_URL}/admin/bulk-email/${bulkJob.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cancellation requested — job will stop shortly');
      setTimeout(() => fetchBulkJobStatus(true), 1500);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to cancel job');
    } finally {
      setCancellingJob(false);
    }
  };

  const fetchAllJobs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/bulk-email/jobs?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data?.jobs || [];
      setAllJobs(list);
      // If nothing selected yet, default to the most recent
      setSelectedJobId(prev => prev || (list[0] && list[0].id) || null);
    } catch (_) { /* non-critical */ }
  };

  // When selectedJobId changes, fetch full job detail
  useEffect(() => {
    if (!selectedJobId) { setBulkJob(null); return; }
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/bulk-email/jobs/${selectedJobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBulkJob(res.data);
      } catch (_) { setBulkJob(null); }
    };
    fetchJob();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId]);

  useEffect(() => {
    fetchCommunicationData();
    fetchAllJobs();
  }, []);

  // Auto-poll the selected bulk job status every 3s when it's running
  useEffect(() => {
    if (!bulkJob || bulkJob.status !== 'running') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/bulk-email/jobs/${bulkJob.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBulkJob(res.data);
        // Also refresh email logs while a job is running
        const logsRes = await axios.get(`${API_URL}/admin/email-logs`, { headers: { Authorization: `Bearer ${token}` } });
        setEmailLogs(logsRes.data?.emails || []);
        setEmailStats(logsRes.data?.stats || { sent: 0, failed: 0, bounced: 0, pending: 0, total: 0 });
      } catch (_) { /* non-critical */ }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJob?.status, bulkJob?.id]);

  const fetchBulkJobStatus = async (refreshLogs = false) => {
    await fetchAllJobs();
    if (refreshLogs) {
      try {
        const logsRes = await axios.get(`${API_URL}/admin/email-logs`, { headers: { Authorization: `Bearer ${token}` } });
        setEmailLogs(logsRes.data?.emails || []);
        setEmailStats(logsRes.data?.stats || { sent: 0, failed: 0, bounced: 0, pending: 0, total: 0 });
      } catch (_) { /* ignore */ }
    }
  };

  const fetchCommunicationData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [countsRes, logsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/user-counts`, { headers }),
        axios.get(`${API_URL}/admin/email-logs`, { headers }),
        axios.get(`${API_URL}/admin/members`, { headers })
      ]);
      setUserCounts(countsRes.data);
      setEmailLogs(logsRes.data.emails || []);
      setEmailStats(logsRes.data.stats || { sent: 0, failed: 0, bounced: 0, pending: 0, total: 0 });
      setCommunicationUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load communication data');
    }
  };

  const handleSendBulkEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    if (emailForm.recipient_type === 'individual' && (!emailForm.individual_emails || emailForm.individual_emails.length === 0)) {
      toast.error('Please select at least one recipient');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API_URL}/admin/send-bulk-email`, emailForm, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      const count = res?.data?.queued ?? res?.data?.sent ?? 0;
      toast.success(`Email queued for ${count} recipients. Watch live progress below.`);
      setEmailForm({ subject: '', message: '', recipient_type: 'all', images: [], individual_emails: [] });
      setEmailImageUrl('');
      setUserSearchQuery('');
      // Immediately fetch job status so the progress card appears
      fetchBulkJobStatus(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
          : 'Failed to queue emails';
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAddEmailImage = () => {
    const url = emailImageUrl.trim();
    if (!url) return;
    if (url.toLowerCase().startsWith('data:')) {
      toast.error('Pasted image data is not supported in emails. Please use the "Upload Image" button instead — Gmail and most email clients strip embedded data URIs.');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Image URL must start with http:// or https://');
      return;
    }
    setEmailForm(prev => ({ ...prev, images: [...prev.images, url] }));
    setEmailImageUrl('');
  };

  const handleRemoveEmailImage = (index) => {
    setEmailForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleEmailImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPEG, PNG and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setUploadingEmailImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/admin/upload`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setEmailForm(prev => ({ ...prev, images: [...prev.images, response.data.image_url] }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingEmailImage(false);
      if (emailImageInputRef.current) emailImageInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Bulk Communications</h2>
      </div>
      
      {/* Recipient Counts */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { type: 'all', label: 'All Users', color: 'text-[#0A2342]' },
          { type: 'members_only', label: 'All Members', color: 'text-blue-600' },
          { type: 'pro', label: 'Pro Members', color: 'text-purple-600' },
          { type: 'club', label: 'Club Members', color: 'text-blue-600' },
          { type: 'non_members', label: 'Non-Members', color: 'text-gray-600' },
        ].map(({ type, label, color }) => (
          <div key={type} className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${emailForm.recipient_type === type ? 'ring-2 ring-[#FF6B35]' : ''}`} onClick={() => setEmailForm({...emailForm, recipient_type: type})}>
            <div className={`text-2xl font-bold ${color}`}>{userCounts[type]}</div>
            <div className="text-sm text-gray-600">{label}</div>
          </div>
        ))}
      </div>

      {/* Email Composer */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Compose Bulk Email
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
            <Select value={emailForm.recipient_type} onValueChange={(v) => setEmailForm({...emailForm, recipient_type: v, individual_emails: []})}>
              <SelectTrigger data-testid="recipient-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users ({userCounts.all})</SelectItem>
                <SelectItem value="members_only">All Members ({userCounts.members_only})</SelectItem>
                <SelectItem value="pro">Pro Members Only ({userCounts.pro})</SelectItem>
                <SelectItem value="club">Club Members Only ({userCounts.club})</SelectItem>
                <SelectItem value="non_members">Non-Members ({userCounts.non_members})</SelectItem>
                <SelectItem value="individual">Select Individuals...</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Individual User Selection */}
          {emailForm.recipient_type === 'individual' && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Recipients ({emailForm.individual_emails.length} selected)
              </label>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Search by name or email..." value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} className="pl-9" data-testid="user-search-input" />
              </div>
              
              {emailForm.individual_emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {emailForm.individual_emails.map((email) => {
                    const user = communicationUsers.find(u => u.email === email);
                    return (
                      <Badge key={email} variant="secondary" className="bg-[#0A2342] text-white flex items-center gap-1">
                        {user ? `${user.first_name} ${user.last_name}` : email}
                        <button type="button" onClick={() => setEmailForm({...emailForm, individual_emails: emailForm.individual_emails.filter(e => e !== email)})} className="ml-1 hover:bg-white/20 rounded-full p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEmailForm({...emailForm, individual_emails: []})} className="text-xs text-red-500 hover:text-red-600">Clear All</Button>
                </div>
              )}
              
              <div className="max-h-48 overflow-y-auto border rounded-md bg-white">
                {communicationUsers
                  .filter(user => {
                    const q = userSearchQuery.toLowerCase();
                    if (!q) return true;
                    return user.email?.toLowerCase().includes(q) || user.first_name?.toLowerCase().includes(q) || user.last_name?.toLowerCase().includes(q) || `${user.first_name} ${user.last_name}`.toLowerCase().includes(q);
                  })
                  .map((user) => (
                    <label key={user.id || user.email} className={`flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-0 ${emailForm.individual_emails.includes(user.email) ? 'bg-blue-50' : ''}`}>
                      <input type="checkbox" checked={emailForm.individual_emails.includes(user.email)} onChange={(e) => {
                        if (e.target.checked) setEmailForm({...emailForm, individual_emails: [...emailForm.individual_emails, user.email]});
                        else setEmailForm({...emailForm, individual_emails: emailForm.individual_emails.filter(em => em !== user.email)});
                      }} className="rounded border-gray-300 text-[#FF6B35] focus:ring-[#FF6B35]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{user.first_name} {user.last_name}</div>
                        <div className="text-xs text-gray-500 truncate">{user.email}</div>
                      </div>
                      {user.membership_tier && (<Badge variant="outline" className="text-xs shrink-0">{user.membership_tier === 'pro' ? 'Pro' : 'Club'}</Badge>)}
                    </label>
                  ))
                }
                {communicationUsers.length === 0 && (<div className="p-4 text-center text-gray-500 text-sm">No users found</div>)}
              </div>
              
              {userSearchQuery && (
                <Button type="button" variant="outline" size="sm" className="mt-2 text-xs" onClick={() => {
                  const filteredEmails = communicationUsers.filter(user => {
                    const q = userSearchQuery.toLowerCase();
                    return user.email?.toLowerCase().includes(q) || user.first_name?.toLowerCase().includes(q) || user.last_name?.toLowerCase().includes(q);
                  }).map(u => u.email);
                  setEmailForm({...emailForm, individual_emails: [...new Set([...emailForm.individual_emails, ...filteredEmails])]});
                }}>Select All Matching</Button>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <Input placeholder="Email subject line" value={emailForm.subject} onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})} data-testid="bulk-email-subject" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea className="w-full border rounded-md p-3 text-sm min-h-[200px]" placeholder="Type your message here... Use {name} to personalize with recipient's name" value={emailForm.message} onChange={(e) => setEmailForm({...emailForm, message: e.target.value})} data-testid="bulk-email-message" />
            <p className="text-xs text-gray-500 mt-1">Tip: Use {'{name}'} to personalize the email with the recipient's first name</p>
          </div>
          
          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Images (Optional)</label>
            <div className="flex gap-2 mb-2">
              <input type="file" ref={emailImageInputRef} onChange={handleEmailImageUpload} accept="image/jpeg,image/png,image/webp" className="hidden" />
              <Button type="button" variant="outline" onClick={() => emailImageInputRef.current?.click()} disabled={uploadingEmailImage} className="shrink-0" data-testid="upload-email-image-btn">
                {uploadingEmailImage ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-1" />Upload Image</>)}
              </Button>
              <Button type="button" variant="outline" onClick={openMediaLibrary} className="shrink-0" data-testid="open-media-library-btn">
                <Image className="w-4 h-4 mr-1" />Library
              </Button>
              <span className="flex items-center text-xs text-gray-400">or</span>
              <Input placeholder="Paste image URL..." value={emailImageUrl} onChange={(e) => setEmailImageUrl(e.target.value)} className="flex-1" data-testid="email-image-url-input" />
              <Button type="button" variant="outline" onClick={handleAddEmailImage} disabled={!emailImageUrl.trim()} data-testid="add-email-image-btn">
                <Plus className="w-4 h-4 mr-1" />Add URL
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">Upload images from your computer or paste image URLs. Images will appear at the top of the email.</p>
            
            {emailForm.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {emailForm.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img src={url} alt={`Email image ${index + 1}`} className="w-full h-24 object-cover rounded-md border" onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL'; }} />
                    <button type="button" onClick={() => handleRemoveEmailImage(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-email-image-${index}`}>x</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <Button onClick={handleSendBulkEmail} disabled={sendingEmail || !emailForm.subject || !emailForm.message || (emailForm.recipient_type === 'individual' && emailForm.individual_emails.length === 0)} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="send-bulk-email-btn">
              {sendingEmail ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>) : (<><Send className="w-4 h-4 mr-2" />Send to {emailForm.recipient_type === 'individual' ? `${emailForm.individual_emails.length} Selected Recipient${emailForm.individual_emails.length !== 1 ? 's' : ''}` : `${userCounts[emailForm.recipient_type] || 0} Recipients`}</>)}
            </Button>
          </div>
        </div>
      </div>

      {/* Live Bulk Send Progress */}
      {allJobs.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-gray-700">Bulk Email Job:</label>
            <Select value={selectedJobId || ''} onValueChange={(v) => setSelectedJobId(v)}>
              <SelectTrigger className="w-auto min-w-[320px] max-w-[600px]" data-testid="bulk-job-selector">
                <SelectValue placeholder="Pick a job..." />
              </SelectTrigger>
              <SelectContent>
                {allJobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    <span className="text-xs">
                      {j.started_at ? new Date(j.started_at).toLocaleString() : ''} · {j.subject || '(no subject)'}
                      {' · '}{j.sent}/{j.total} sent
                      {(j.unretried_failed || 0) > 0 ? ` · ${j.unretried_failed} unretried` : ''}
                      {j.retry_of_job_id ? ' · retry' : ''}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => fetchBulkJobStatus(true)} data-testid="bulk-jobs-refresh">Refresh</Button>
          </div>
        </div>
      )}

      {bulkJob && bulkJob.id && (
        <div className={`bg-white rounded-lg border p-5 ${bulkJob.status === 'running' ? 'border-[#FF6B35]' : 'border-gray-200'}`} data-testid="bulk-progress-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
                  {bulkJob.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-[#FF6B35]" />}
                  Bulk Email Job
                </h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  bulkJob.status === 'running' ? 'bg-orange-100 text-orange-700'
                  : bulkJob.status === 'complete' ? 'bg-green-100 text-green-700'
                  : bulkJob.status === 'cancelled' ? 'bg-gray-200 text-gray-700'
                  : 'bg-red-100 text-red-700'
                }`}>
                  {bulkJob.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 truncate" title={bulkJob.subject}>{bulkJob.subject}</p>
              <p className="text-xs text-gray-400 mt-0.5">Started {bulkJob.started_at ? new Date(bulkJob.started_at).toLocaleString() : '-'}</p>
            </div>
            <div className="flex items-center gap-2">
              {bulkJob.status === 'running' && (
                <Button size="sm" variant="outline" onClick={handleCancelJob} disabled={cancellingJob} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="cancel-bulk-job-btn">
                  {cancellingJob ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Cancel'}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => fetchBulkJobStatus(true)} data-testid="bulk-progress-refresh">
                Refresh
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>
                {(bulkJob.current_index || 0)} of {bulkJob.total || 0} processed
              </span>
              <span>
                <span className="text-green-600 font-medium">{bulkJob.sent || 0} sent</span>
                {bulkJob.failed ? <span className="text-red-600 font-medium ml-3">{bulkJob.failed} failed</span> : null}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all ${bulkJob.status === 'failed' ? 'bg-red-500' : 'bg-[#FF6B35]'}`}
                style={{ width: `${bulkJob.total ? Math.min(100, Math.round(((bulkJob.current_index || 0) / bulkJob.total) * 100)) : 0}%` }}
              />
            </div>
          </div>

          {/* Live next-up email */}
          {bulkJob.status === 'running' && bulkJob.current_email && (
            <div className="mt-3 text-sm">
              <span className="text-gray-500">Now sending → </span>
              <span className="font-medium text-[#0A2342]" data-testid="bulk-current-email">{bulkJob.current_email}</span>
            </div>
          )}
          {bulkJob.status === 'complete' && (
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-green-700">Job complete. {bulkJob.sent} delivered, {bulkJob.failed || 0} failed.{(bulkJob.unretried_failed || 0) > 0 ? ` ${bulkJob.unretried_failed} not yet retried.` : ''}</div>
              {(bulkJob.unretried_failed || 0) > 0 && (
                <Button
                  size="sm"
                  onClick={handleRetryFailed}
                  disabled={retryingFailed}
                  className="bg-[#FF6B35] hover:bg-[#FF6B35]/90"
                  data-testid="retry-failed-emails-btn"
                >
                  {retryingFailed ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Re-queuing...</>
                  ) : (
                    <><RotateCcw className="w-4 h-4 mr-2" />Re-send to {bulkJob.unretried_failed} Failed Only</>
                  )}
                </Button>
              )}
            </div>
          )}
          {bulkJob.status === 'failed' && bulkJob.error_message && (
            <div className="mt-3 text-sm text-red-700">Error: {bulkJob.error_message}</div>
          )}
        </div>
      )}

      {/* Email History Section */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
          <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
            <Mail className="w-4 h-4" /> Email History
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">{emailStats.sent} Sent</span>
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">{emailStats.failed} Failed</span>
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">{emailStats.bounced} Bounced</span>
            </div>
            <Select value={emailLogFilter} onValueChange={setEmailLogFilter}>
              <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={async () => {
              setLoadingEmailLogs(true);
              try {
                const res = await axios.get(`${API_URL}/admin/email-logs`, { headers: { Authorization: `Bearer ${token}` } });
                setEmailLogs(res.data.emails || []);
                setEmailStats(res.data.stats || { sent: 0, failed: 0, bounced: 0, pending: 0, total: 0 });
              } catch (error) { toast.error('Failed to refresh email logs'); }
              setLoadingEmailLogs(false);
            }} data-testid="refresh-email-logs-btn">
              {loadingEmailLogs ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left p-3 font-medium text-gray-600">Recipient</th>
                <th className="text-left p-3 font-medium text-gray-600">Subject</th>
                <th className="text-left p-3 font-medium text-gray-600">Type</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-left p-3 font-medium text-gray-600">Date</th>
                <th className="text-left p-3 font-medium text-gray-600">Error</th>
                <th className="text-left p-3 font-medium text-gray-600">View</th>
              </tr>
            </thead>
            <tbody>
              {emailLogs.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500">No emails sent yet. Send your first bulk email above!</td></tr>
              ) : (
                emailLogs.filter(log => emailLogFilter === 'all' || log.status === emailLogFilter).map((log, idx) => (
                  <tr key={log.id || idx} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => openEmailView(log.id)} data-testid={`email-log-row-${idx}`}>
                    <td className="p-3 font-medium">{log.to_email}</td>
                    <td className="p-3 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                    <td className="p-3"><span className="text-xs px-2 py-1 bg-gray-100 rounded">{log.email_type?.replace(/_/g, ' ') || 'general'}</span></td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${log.status === 'sent' ? 'bg-green-100 text-green-700' : log.status === 'failed' ? 'bg-red-100 text-red-700' : log.status === 'bounced' ? 'bg-orange-100 text-orange-700' : log.status === 'superseded' ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-700'}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-xs">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                    <td className="p-3 text-xs text-red-600 max-w-xs truncate" title={log.error_message || ''}>{log.error_message || '-'}</td>
                    <td className="p-3">
                      <button type="button" onClick={(e) => { e.stopPropagation(); openEmailView(log.id); }} className="text-[#FF6B35] hover:text-[#FF6B35]/80" data-testid={`view-email-btn-${idx}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Media Library Modal */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="media-library-modal">
          <DialogHeader>
            <DialogTitle className="text-[#0A2342] flex items-center gap-2">
              <Image className="w-5 h-5" /> Media Library
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Browse, reuse, or delete past email images. Click an image to add it to your current email.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto mt-3 -mx-2 px-2">
            {mediaLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B35]" /></div>
            ) : mediaItems.length === 0 ? (
              <div className="text-center text-gray-500 py-12 text-sm">
                No images uploaded yet. Use "Upload Image" above to add your first one.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {mediaItems.map((m) => (
                  <div key={m.filename} className="border rounded-lg overflow-hidden bg-white group" data-testid={`media-item-${m.filename}`}>
                    <div className="relative aspect-square bg-gray-50 cursor-pointer" onClick={() => addMediaToEmail(m.url)} title="Click to add to email">
                      <img src={m.url} alt={m.filename} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      {m.persistent && (
                        <span
                          className="absolute top-1.5 left-1.5 text-[10px] font-semibold tracking-wide bg-emerald-600/90 text-white px-1.5 py-0.5 rounded-sm flex items-center gap-0.5 shadow-sm"
                          title="Stored in Emergent Object Store — survives redeploys"
                          data-testid={`media-permanent-${m.filename}`}
                        >
                          <Check className="w-2.5 h-2.5" strokeWidth={3} /> Permanent
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="text-white text-xs font-medium bg-[#FF6B35] px-3 py-1 rounded-full">+ Add to email</span>
                      </div>
                    </div>
                    <div className="p-2 text-xs">
                      <div className="text-gray-600 truncate" title={m.filename}>{m.filename}</div>
                      <div className="text-gray-400 flex justify-between mt-1">
                        <span>{formatFileSize(m.size_bytes)}</span>
                        <span>{m.uploaded_at ? new Date(m.uploaded_at).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={(e) => { e.stopPropagation(); copyMediaUrl(m.url); }} data-testid={`copy-media-${m.filename}`}>
                          <Copy className="w-3 h-3 mr-1" /> URL
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); deleteMediaItem(m.filename); }}
                          disabled={mediaDeleting === m.filename}
                          data-testid={`delete-media-${m.filename}`}
                        >
                          {mediaDeleting === m.filename ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Email Modal */}
      <Dialog open={!!viewEmail} onOpenChange={(open) => { if (!open) setViewEmail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="view-email-modal">
          <DialogHeader>
            <DialogTitle className="text-[#0A2342]">
              {viewEmail?.loading ? 'Loading…' : (viewEmail?.subject || 'Email Details')}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              {viewEmail && !viewEmail.loading && (
                <>
                  <div><strong>To:</strong> {viewEmail.to_email}</div>
                  <div><strong>From:</strong> {viewEmail.from_email || 'Just Dive'}</div>
                  <div><strong>Status:</strong> <span className={`font-medium ${viewEmail.status === 'sent' ? 'text-green-600' : 'text-red-600'}`}>{viewEmail.status}</span></div>
                  <div><strong>Sent:</strong> {viewEmail.created_at ? new Date(viewEmail.created_at).toLocaleString() : '-'}</div>
                  {viewEmail.error_message && (<div className="text-red-600 mt-1"><strong>Error:</strong> {viewEmail.error_message}</div>)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded-md bg-gray-50 p-3 mt-2" data-testid="view-email-body">
            {viewEmail?.loading || loadingViewEmail ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B35]" /></div>
            ) : viewEmail?.html_content ? (
              <iframe
                title="Email preview"
                srcDoc={viewEmail.html_content}
                sandbox=""
                className="w-full h-[60vh] bg-white border rounded"
              />
            ) : (
              <div className="text-center text-gray-500 py-8 text-sm">No HTML body stored for this email.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationsTab;
